import { experience, type ConsentInfoResponse } from '@logto/schemas';
import { useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import StaticPageLayout from '@/Layout/StaticPageLayout';
import PageContext from '@/Providers/PageContextProvider/PageContext';
import { consent, getConsentInfo } from '@/apis/consent';
import { getSessionSlots, selectSessionSlot, type SlotAccount } from '@/apis/session-slots';
import TextLink from '@/components/TextLink';
import useApi from '@/hooks/use-api';
import useErrorHandler from '@/hooks/use-error-handler';
import useNavigateWithPreservedSearchParams from '@/hooks/use-navigate-with-preserved-search-params';
import UserProfile from '@/pages/Consent/UserProfile';
import { getRedirectUriOrigin } from '@/pages/Consent/util';
import Button from '@/shared/components/Button';
import DynamicT from '@/shared/components/DynamicT';
import LoadingLayer from '@/shared/components/LoadingLayer';
import PageMeta from '@/shared/components/PageMeta';
import { getBrandingLogoUrl } from '@/shared/utils/logo';

import styles from './index.module.scss';

/**
 * SwitchAccount page — three modes:
 *
 * Mode 1 (login_hint present): Original magic-link behavior.
 *   User has a session, an OTT link targets a different account.
 *   Shows "Continue as [login_hint]" (→ OTT page) + "Back to current account".
 *
 * Mode 2 (no login_hint, prompt=select_account, registry has ≥2 accounts):
 *   TPB multi-account picker (Connect2id-style). Lists all accounts known
 *   by the browser registry + "Sign in with a different account".
 *
 * Mode 3 (no login_hint, no registry or only 1 account):
 *   Fallback to single-account UX. Shows current session + "Continue as X"
 *   + "Sign in with a different account".
 */
const SwitchAccount = () => {
  const { experienceSettings, theme } = useContext(PageContext);
  const navigate = useNavigateWithPreservedSearchParams();
  const handleError = useErrorHandler();

  const [consentData, setConsentData] = useState<ConsentInfoResponse>();
  const asyncGetConsentInfo = useApi(getConsentInfo);
  const asyncConsent = useApi(consent);

  const [params] = useSearchParams();
  const loginHint = params.get('login_hint');

  const [registryAccounts, setRegistryAccounts] = useState<SlotAccount[]>([]);
  const [registryLoading, setRegistryLoading] = useState(!loginHint);
  const [switchPending, setSwitchPending] = useState(false);

  useEffect(() => {
    (async () => {
      const [error, result] = await asyncGetConsentInfo();
      if (error) {
        // The consent endpoint asserts an active oidc-provider session via
        // `interactionDetails.session`. When the user lands on /switch-account
        // with no current session (registry empty after a logout, all slots
        // expired, IDP redeploy invalidated cookies, etc.), this assertion
        // fails and oidc-provider returns 400 `session.not_found`. Without
        // recovery the user is stuck on a "Session not found" screen with no
        // way out.
        //
        // Recovery: redirect to /sign-in so oidc-provider's interaction picks
        // up the standard sign-in flow. The fresh login captures into a new
        // slot via the translator post-handler, and the original
        // `prompt=select_account` resolves through resume normally.
        navigate({ pathname: `/${experience.routes.signIn}` }, { replace: true });
        return;
      }
      setConsentData(result);
    })();
  }, [asyncGetConsentInfo, handleError, navigate]);

  useEffect(() => {
    if (loginHint) return;
    (async () => {
      try {
        const accounts = await getSessionSlots();
        setRegistryAccounts(accounts);
      } catch {
        // Registry empty or unavailable — fall back to single-account UX
        setRegistryAccounts([]);
      } finally {
        setRegistryLoading(false);
      }
    })();
  }, [loginHint]);

  if (!experienceSettings || !consentData) {
    return <LoadingLayer />;
  }
  if (!loginHint && registryLoading) {
    return <LoadingLayer />;
  }

  const {
    color: { isDarkModeEnabled },
    branding,
  } = experienceSettings;
  const logoUrl = getBrandingLogoUrl({ theme, branding, isDarkModeEnabled });
  const redirectUriOrigin = consentData.redirectUri
    ? getRedirectUriOrigin(consentData.redirectUri)
    : undefined;

  const finishConsent = async () => {
    const [error, result] = await asyncConsent();
    if (error) {
      await handleError(error);
      return;
    }
    if (result?.redirectTo) {
      window.location.replace(result.redirectTo);
    }
  };

  // Mode 1: login_hint present — original OTT magic-link behavior
  if (loginHint) {
    return (
      <StaticPageLayout>
        <PageMeta titleKey="description.switch_account" />
        <div className={styles.container}>
          {logoUrl && <img className={styles.logo} src={logoUrl} alt="app logo" />}
          <div className={styles.title}>
            <DynamicT
              forKey="description.switch_account_title"
              interpolation={{ account: consentData.user.primaryEmail }}
            />
          </div>
          <UserProfile user={consentData.user} className={styles.userProfile} />
          <div className={styles.message}>
            <DynamicT forKey="description.switch_account_description" />
          </div>
          <Button
            className={styles.button}
            type="primary"
            size="large"
            title="action.continue_as"
            i18nProps={{ name: loginHint }}
            onClick={() => {
              navigate(
                { pathname: `/${experience.routes.oneTimeToken}`, search: `?${params.toString()}` },
                { replace: true }
              );
            }}
          />
          <div className={styles.linkButton}>
            <TextLink
              text="action.back_to_current_account"
              onClick={() => {
                if (redirectUriOrigin) {
                  window.location.assign(redirectUriOrigin);
                }
              }}
            />
          </div>
        </div>
      </StaticPageLayout>
    );
  }

  // Mode 2: registry has ≥2 accounts → TPB multi-account picker
  if (registryAccounts.length >= 2) {
    return (
      <StaticPageLayout>
        <PageMeta titleKey="description.switch_account" />
        <div className={styles.container}>
          {logoUrl && <img className={styles.logo} src={logoUrl} alt="app logo" />}
          <div className={styles.title}>
            <DynamicT forKey="description.choose_account" />
          </div>

          {registryAccounts.map((account) => (
            <Button
              key={account.slot}
              className={styles.button}
              type={account.isCurrent ? 'primary' : 'secondary'}
              size="large"
              title="action.continue_as"
              i18nProps={{ name: account.email ?? account.name ?? account.accountId }}
              isLoading={switchPending}
              onClick={async () => {
                if (switchPending) return;
                setSwitchPending(true);
                try {
                  if (!account.isCurrent) {
                    await selectSessionSlot(account.slot);
                  }
                  await finishConsent();
                } finally {
                  setSwitchPending(false);
                }
              }}
            />
          ))}

          <div className={styles.linkButton}>
            <TextLink
              text="action.sign_in_with_different_account"
              onClick={() => {
                navigate({ pathname: `/${experience.routes.signIn}` }, { replace: true });
              }}
            />
          </div>
        </div>
      </StaticPageLayout>
    );
  }

  // Mode 3 (fallback): no registry or only 1 account — single-session UX
  const currentEmail = consentData.user.primaryEmail ?? consentData.user.name ?? 'current account';

  return (
    <StaticPageLayout>
      <PageMeta titleKey="description.switch_account" />
      <div className={styles.container}>
        {logoUrl && <img className={styles.logo} src={logoUrl} alt="app logo" />}
        <div className={styles.title}>
          <DynamicT forKey="description.switch_account" />
        </div>
        <UserProfile user={consentData.user} className={styles.userProfile} />
        <Button
          className={styles.button}
          type="primary"
          size="large"
          title="action.continue_as"
          i18nProps={{ name: currentEmail }}
          onClick={finishConsent}
        />
        <div className={styles.linkButton}>
          <TextLink
            text="action.sign_in_with_different_account"
            onClick={() => {
              navigate({ pathname: `/${experience.routes.signIn}` }, { replace: true });
            }}
          />
        </div>
      </div>
    </StaticPageLayout>
  );
};

export default SwitchAccount;
